import React, { useId } from "react";

type ArchTextProps = {
    text: string;
    radius?: number; // radius of the arc
    startAngle?: number; // degrees, where 0 is to the right (like unit circle)
    endAngle?: number; // degrees
    fontSize?: number; // px
    inner?: boolean; // if true, text follows inner side of arc (flipped)
    fontStyle?: React.CSSProperties; // additional font styles
    textColor?: string;
    className?: string;
    width?: number; // optional svg width override
    height?: number; // optional svg height override
    textAnchor?: "start" | "middle" | "end"; // controls textPath textAnchor
};

// convert degrees to radians helper
const deg2rad = (d: number) => (d * Math.PI) / 180;

// convert polar to cartesian (SVG coords: +x right, +y down)
function polarToCartesian(cx: number, cy: number, radius: number, angleInDegrees: number) {
    const angleInRadians = deg2rad(angleInDegrees - 90);
    return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians),
    };
}

// Create an SVG arc path from startAngle to endAngle
function describeArc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = Math.abs(endAngle - startAngle) <= 180 ? "0" : "1";
    const sweepFlag = endAngle > startAngle ? "1" : "0";
    return [
        "M",
        start.x,
        start.y,
        "A",
        radius,
        radius,
        0,
        largeArcFlag,
        sweepFlag,
        end.x,
        end.y,
    ].join(" ");
}

export default function ArchText({
                                     text,
                                     radius = 120,
                                     startAngle = -90,
                                     endAngle = 90,
                                     fontSize = 18,
                                     inner = false,
                                     fontStyle = { fontFamily: "sans-serif" },
                                     textColor = "#000",
                                     className,
                                     width,
                                     height,
                                     textAnchor = "middle",
                                 }: ArchTextProps) {
    // center the arc inside the SVG
    const diameter = radius * 2 + fontSize * 2; // some padding for the text
    const svgW = width ?? diameter;
    const svgH = height ?? diameter;
    const cx = svgW / 2;
    const cy = svgH / 2;

    // unique id for the path so multiple components can exist on a page
    const id = useId();
    const pathId = `arch-path-${id}`;

    // If user wants inner arc (text on inner side), we draw the arc with slightly smaller radius
    // and reverse the path direction so the text is flipped to follow the inner side.
    const pad = fontSize / 2;
    const effectiveRadius = Math.max(1, inner ? radius - pad : radius + pad); // ensure positive radius

    // When inner is true, swap start and end so path direction is reversed (this flips text along the path)
    const pathStart = inner ? endAngle : startAngle;
    const pathEnd = inner ? startAngle : endAngle;

    const arcPath = describeArc(cx, cy, effectiveRadius, pathStart, pathEnd);

    // If inner, flip the text with textPath method by setting side and startOffset
    // We'll attempt to center the text by startOffset="50%" and textAnchor="middle"

    return (
        <svg
            className={className}
            width={svgW}
            height={svgH}
            viewBox={`0 0 ${svgW} ${svgH}`}
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <path id={pathId} d={arcPath} fill="none" />
            </defs>

            {/* Optionally display the path for debugging: uncomment to see the arc */}
            {/* <use href={`#${pathId}`} stroke="#ddd" strokeWidth={1} fill="none" /> */}

            <text
                fontSize={fontSize}
                style={fontStyle}
                dominantBaseline="middle"
            >
                <textPath
                    href={`#${pathId}`}
                    startOffset="50%"
                    textAnchor={textAnchor}
                    fill={textColor}
                >
                    {text}
                </textPath>
            </text>
        </svg>
    );
}